import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ColumnFilterRow, applyColumnFilters } from "../../Components/Common/DashboardKit";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import TablePager, { usePagedRows } from "./_shared/TablePager";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { MafiaPageHeader, TableSkeleton, EmptyState } from "./_shared";
import MafiaTheme from "./MafiaTheme";
import { highlightMatch, MafiaHighlightStyle } from "./mafiaUi";
import { resolveEmployeeNames } from "./mafiaIdentity";
import { partRevisionText } from "./mafiaPrintHeading";

const REFRESH_INTERVAL_MS = 60000;

export default function Unreleased({ onBack, onOpenReleasePart }) {
  const dialog = useAppDialog();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKey, setSelectedRowKey] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const searchInputRef = useRef(null);
  const rowRefs = useRef({});
  const mountedRef = useRef(true);

  const selectedRow = useMemo(() => {
    return rows.find((row) => getRowKey(row) === selectedRowKey) || null;
  }, [rows, selectedRowKey]);

  const selectedPart = useMemo(() => {
    if (!selectedRow) {
      return {
        partNum: "",
        rev: 0,
        revMinor: 0,
      };
    }

    return {
      partNum: String(selectedRow.partNumber ?? ""),
      rev: Number(selectedRow.printRevision ?? 0),
      revMinor: Number(selectedRow.mafiaRevision ?? 0),
    };
  }, [selectedRow]);

  const displayRows = useMemo(() => {
    const search = searchText.trim().toUpperCase();
    if (!search) return rows;

    return rows.filter((row) => {
      const partNumber = String(row.partNumber ?? "").toUpperCase();
      const printRevision = String(row.printRevision ?? "").toUpperCase();
      const mafiaRevision = String(row.mafiaRevision ?? "").toUpperCase();
      const by = String(row.by ?? "").toUpperCase();
      const timeStamp = formatDateTime(row.timeStamp).toUpperCase();

      return (
        partNumber.includes(search) ||
        printRevision.includes(search) ||
        mafiaRevision.includes(search) ||
        by.includes(search) ||
        timeStamp.includes(search)
      );
    });
  }, [rows, searchText]);

  // Round 36 — EPO-style per-column filters + click-to-sort + CSV.
  const [columnFilters, setColumnFilters] = React.useState({});
  const mafiaColumns = React.useMemo(() => [
    { id: "partNumber",    label: "Part Number",    type: "text", get: (r) => r.partNumber || "" },
    { id: "printRevision", label: "Part Revision", type: "text", get: (r) => partRevisionText(r.printRevision) },
    { id: "mafiaRevision", label: "MAFIA Revision", type: "text", get: (r) => String(r.mafiaRevision ?? "") },
    { id: "by",            label: "By",             type: "text", get: (r) => r.by || "" },
    { id: "timeStamp",     label: "Timestamp",      type: "date", get: (r) => r.timeStamp || "" },
  ], []);
  const viewBase = applyColumnFilters(displayRows, mafiaColumns, columnFilters);
  const { sorted: viewRows, sort, toggleSort } = useTableSort(viewBase, mafiaColumns);

  // Round 59 (search fix): the table header has a leading "Select" column,
  // but the filter row was rendered from mafiaColumns alone — so every
  // filter input sat ONE COLUMN LEFT of its header (the box under "Part
  // Number" was actually filtering Drawing Revision, etc.), which is why
  // search "didn't work". The filter row now gets an explicit empty cell
  // for Select. mafiaColumns itself is unchanged (sorting/CSV unaffected).
  const filterColumns = React.useMemo(
    () => [{ id: "sel", label: "Select", type: "none" }, ...mafiaColumns],
    [mafiaColumns]
  );

  // Round 61 (UX): the filter row also spans the trailing per-row action
  // column added this round.
  const filterColumnsWithAction = React.useMemo(
    () => [...filterColumns, { id: "act", label: "", type: "none" }],
    [filterColumns]
  );

  // Round 61 (UX): active-filter awareness — a visible count and one-click
  // clear (data-table UX guidance: filter state must be visible and easy to
  // reset; don't rely on users emptying each box).
  const activeFilterCount = React.useMemo(
    () => Object.values(columnFilters || {}).filter((v) => v != null && v !== "").length,
    [columnFilters]
  );

  // Round 60 — client-side pagination (561+ rows previously rendered at
  // once). Filters/sort/CSV still operate on the FULL viewRows list; only
  // the rendered slice is paged. Page snaps to 1 when filters/search/sort
  // change; the auto-refresh keeps the current page (clamped if the list
  // shrinks).
  const pager = usePagedRows(
    viewRows,
    JSON.stringify({ f: columnFilters, q: searchText, s: sort })
  );

  const refreshData = useCallback(
    async (showSpinner = true) => {
      try {
        if (showSpinner) {
          setRefreshing(true);
        }

        const data = await getUnreleasedParts();
        if (!mountedRef.current) return;

        const safeRows = data || [];
        setRows(safeRows);

        if (selectedRowKey) {
          const stillExists = safeRows.some((row) => getRowKey(row) === selectedRowKey);
          if (!stillExists) {
            setSelectedRowKey("");
          }
        }
      } catch (error) {
        console.error("Failed to refresh unreleased parts:", error);
        if (showSpinner) {
          await dialog.error("Failed to refresh unreleased parts.");
        }
      } finally {
        if (mountedRef.current && showSpinner) {
          setRefreshing(false);
        }
      }
    },
    [selectedRowKey, dialog]
  );

  useEffect(() => {
    mountedRef.current = true;

    async function loadInitial() {
      try {
        const data = await getUnreleasedParts();
        if (!mountedRef.current) return;
        setRows(data || []);
      } catch (error) {
        console.error("Failed to load unreleased parts:", error);
        await dialog.error("Failed to load unreleased parts list.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          if (searchInputRef.current) {
            searchInputRef.current.focus();
            searchInputRef.current.select();
          }
        }
      }
    }

    loadInitial();

    return () => {
      mountedRef.current = false;
    };
  }, [dialog]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      refreshData(false);
    }, REFRESH_INTERVAL_MS);

    return () => window.clearInterval(timerId);
  }, [refreshData]);

  function handleRowSelect(row) {
    if (!row) return;
    setSelectedRowKey(getRowKey(row));
  }

  async function openReleasePart(partData = selectedPart) {
    if (!partData.partNum) {
      await dialog.warning("Please select a part first.");
      return;
    }

    try {
      if (typeof onOpenReleasePart === "function") {
        onOpenReleasePart({
          partNum: partData.partNum,
          rev: partData.rev,
          revMinor: partData.revMinor,
        });
      } else {
        console.warn("Release Part callback is not configured.");
        await dialog.error("Release Part page is not configured.");
      }
    } catch (error) {
      console.error("Failed to open release part:", error);
      await dialog.error("Failed to open release part.");
    }
  }

  function handleSearchKeyDown(e) {
    if (e.key !== "Enter") return;

    if (displayRows.length > 0) {
      const firstMatch = displayRows[0];
      const rowKey = getRowKey(firstMatch);

      setSelectedRowKey(rowKey);

      const rowEl = rowRefs.current[rowKey];
      if (rowEl) {
        rowEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }

    if (searchInputRef.current) {
      searchInputRef.current.select();
    }
  }

  return (
    <div className="unreleased-page mafia-screen">
      <MafiaTheme />
      <MafiaHighlightStyle />
      <MafiaPageHeader
        icon="ri-rocket-line"
        eyebrow="MAFIA Aux"
        title="Unreleased Parts"
        subtitle="Verified parts waiting to be released. Double-click a row to open Release Part."
        tone="violet"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back to Menu
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Unreleased Parts" },
        ]}
      />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="unreleased-toolbar-main">
                <div className="unreleased-toolbar-main-left">
                  <Button
                    color="success"
                    className="unreleased-action-btn"
                    onClick={() => openReleasePart()}
                    disabled={!selectedPart.partNum}
                  >
                    <i className="ri-rocket-line unreleased-btn-icon me-1" />
                    Open Release Part
                  </Button>
                </div>

                <div className="unreleased-toolbar-main-right">
                  {/* Global search removed — use the per-column filter boxes under
                      the headers (client-side, searches the full loaded list). */}
                  <span className="text-muted small">
                    <i className="ri-filter-3-line me-1" />
                    Filter using the column boxes below
                  </span>
                </div>
              </div>

              <div className="unreleased-toolbar-utility">
                <Button
                  color="primary"
                  className="unreleased-action-btn unreleased-utility-btn"
                  onClick={() => refreshData(true)}
                  disabled={refreshing}
                >
                  <i className="ri-refresh-line unreleased-btn-icon me-1" />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>

                <Badge color="light" className="unreleased-queue-badge">
                  Queue Size: {displayRows.length}
                </Badge>

                <div className="unreleased-info-pill">
                  <span className="unreleased-info-label">Selected Part</span>
                  <span
                    className="unreleased-info-value"
                    title={selectedRow?.partNumber || "No selection"}
                  >
                    {selectedRow?.partNumber || "No selection"}
                  </span>
                </div>

                <div className="unreleased-info-pill">
                  <span className="unreleased-info-label">Revision</span>
                  <span className="unreleased-info-value">
                    {selectedRow
                      ? `Part ${partRevisionText(selectedRow.printRevision)} · MAFIA ${selectedRow.mafiaRevision ?? 0}`
                      : "—"}
                  </span>
                </div>

                <div className="unreleased-refresh-note">
                  Auto refresh every 1 min
                </div>
              </div>
            </CardHeader>

            <CardBody className="p-4">
              <div className="unreleased-table-shell">
                <div className="unreleased-table-scroll">
                  {/* Round 36 — CSV of the filtered + sorted list */}
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <span className="text-muted small">
                      {activeFilterCount > 0
                        ? `${viewRows.length.toLocaleString()} of ${rows.length.toLocaleString()} rows match the active filters`
                        : `${rows.length.toLocaleString()} rows — filter using the boxes below the headers`}
                    </span>
                    <div className="d-flex gap-2">
                      {activeFilterCount > 0 && (
                        <button type="button" className="btn btn-soft-secondary btn-sm"
                          onClick={() => setColumnFilters({})}>
                          <i className="ri-filter-off-line align-bottom me-1" />
                          Clear filters ({activeFilterCount})
                        </button>
                      )}
                      <button type="button" className="btn btn-soft-primary btn-sm"
                        onClick={() => exportRowsToCsv("mafia-unreleased-parts.csv", mafiaColumns, viewRows)}>
                        <i className="ri-download-2-line align-bottom me-1" />Export CSV ({viewRows.length})
                      </button>
                    </div>
                  </div>
                  <Table responsive={false} className="align-middle mb-0 unreleased-table">
                    <thead className="table-light">
                      <tr>
                        <th className="unreleased-col-select">Select</th>
                        {mafiaColumns.map((c) => (
                          <SortableTh key={c.id} col={c} sort={sort} onToggle={toggleSort} />
                        ))}
                        {/* Round 61 (UX): visible per-row action — the open
                            gesture was ONLY a double-click, which nothing on
                            the screen advertised. */}
                        <th className="unreleased-col-action text-end">Open</th>
                      </tr>
                      <ColumnFilterRow columns={filterColumnsWithAction} filters={columnFilters} onChange={setColumnFilters} live />
                    </thead>
                    <tbody>
                      {loading ? (
                        <TableSkeleton rows={6} columns={7} />
                      ) : viewRows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="p-0">
                            <EmptyState
                              icon="ri-rocket-line"
                              title="No unreleased parts"
                              message="All verified parts have been released. New ones will appear here as they're confirmed."
                              tone="success"
                            />
                          </td>
                        </tr>
                      ) : (
                        pager.pagedRows.map((row) => {
                          const rowKey = getRowKey(row);
                          const isSelected = rowKey === selectedRowKey;

                          return (
                            <tr
                              key={rowKey}
                              ref={(el) => {
                                if (el) {
                                  rowRefs.current[rowKey] = el;
                                }
                              }}
                              tabIndex={0}
                              onClick={() => handleRowSelect(row)}
                              onDoubleClick={() =>
                                openReleasePart({
                                  partNum: String(row.partNumber ?? ""),
                                  rev: Number(row.printRevision ?? 0),
                                  revMinor: Number(row.mafiaRevision ?? 0),
                                })
                              }
                              className={`unreleased-data-row ${isSelected ? "selected" : ""}`}
                            >
                              <td className="unreleased-col-select">
                                <Input
                                  type="radio"
                                  checked={isSelected}
                                  onChange={() => handleRowSelect(row)}
                                  name="selected-unreleased-part-row"
                                />
                              </td>
                              <td>
                                <div className="fw-semibold text-dark">{highlightMatch(row.partNumber, searchText)}</div>
                              </td>
                              <td>{partRevisionText(row.printRevision)}</td>
                              <td>{row.mafiaRevision}</td>
                              <td>{row.by}</td>
                              <td>{formatDateTime(row.timeStamp)}</td>
                              <td className="text-end">
                                <Button
                                  color="light"
                                  size="sm"
                                  className="border"
                                  title="Open Release Part"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openReleasePart({
                                      partNum: String(row.partNumber ?? ""),
                                      rev: Number(row.printRevision ?? 0),
                                      revMinor: Number(row.mafiaRevision ?? 0),
                                    });
                                  }}
                                >
                                  <i className="ri-rocket-line align-bottom me-1" />
                                  Release
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
                <TablePager {...pager} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .unreleased-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .unreleased-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .unreleased-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .unreleased-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .unreleased-search-group {
          width: 100%;
        }

        .unreleased-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .unreleased-action-btn:disabled,
          .unreleased-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


        .unreleased-utility-btn {
            min-width: 110px;
          }

        .unreleased-btn-icon {
          font-size: 0.95rem;
        }

        .unreleased-queue-badge {
            display: inline-flex;
            align-items: center;
            min-height: 32px;
            border: 1px solid #e9ebec;
            padding: 0.3rem 0.65rem;
            border-radius: 0.375rem;
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            background: #f3f6f9;
          }

        .unreleased-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

        .unreleased-info-label {
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #878a99;
            white-space: nowrap;
          }

        .unreleased-info-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

        .unreleased-refresh-note {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.2rem;
          font-size: 0.84rem;
          color: #878a99;
          font-weight: 500;
        }

        .btn-soft-dark {
          background: #eff2f7;
          color: #212529;
          border: 1px solid #e9ebec;
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: #e9ebec;
          color: #212529;
          border-color: #dee2e6;
        }

        .unreleased-table-shell {
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          background: #fff;
        }

        .unreleased-table-scroll {
          max-height: calc(100vh - 240px);
          min-height: 320px;
          overflow-x: auto;
          overflow-y: auto;
        }

        .unreleased-table {
          min-width: 980px;
        }

        .unreleased-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f3f6f9;
          white-space: nowrap;
        }

        .unreleased-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #495057;
          vertical-align: middle;
          white-space: nowrap;
        }

        .unreleased-col-select {
          width: 72px;
          text-align: center;
        }

        .unreleased-data-row {
          transition: background-color 0.18s ease, box-shadow 0.18s ease;
          cursor: pointer;
        }

        .unreleased-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .unreleased-data-row:hover {
          background: #f5f9ff;
        }

        .unreleased-data-row.selected {
          background: rgba(13, 110, 253, 0.08);
          box-shadow: inset 4px 0 0 #0d6efd;
        }

        @media (max-width: 1199.98px) {
          .unreleased-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .unreleased-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .unreleased-table-scroll {
            max-height: 460px;
          }

          .unreleased-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .unreleased-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function getRowKey(row) {
  return `${row.partNumber ?? ""}-${row.printRevision ?? 0}-${row.mafiaRevision ?? 0}`;
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

async function getUnreleasedParts() {
  const response = await api.get("mafia/unreleased-parts");

  const items = (response.data || []).map((item) => ({
    partNumber: item.partNumber,
    printRevision: item.printRevision,
    mafiaRevision: item.mafiaRevision,
    by: item.by,
    timeStamp: item.timeStamp,
  }));

  // Go-live parity (2026-09-24, audit N7): show the person's name in the
  // "By" column ("First Last (id)") instead of the raw EmpId / Windows user
  // name. One batched, cached lookup; on failure the raw value stays.
  try {
    const names = await resolveEmployeeNames(items.map((item) => item.by));
    items.forEach((item) => {
      const raw = item.by == null ? "" : String(item.by).trim();
      if (raw && names.get(raw)) item.by = names.get(raw);
    });
  } catch (_) { /* keep raw values */ }

  return items;
}